import * as THREE from "three";
import { createBlocks, type Block } from "./blocks";
import { initKeyboard } from "./input/keyboard";
import type { KeyAction, Mode } from "./input/keys";
import { initPointer } from "./input/pointer";
import { measure, touchedDown } from "./physics/body";
import { MATERIAL_DEFS, SHAPES } from "./physics/materials";
import { createWorld, STEP, stepFixed, type Rapier } from "./physics/world";
import { createCameraRig } from "./render/camera";
import { createMaterials } from "./render/materials";
import { createScene } from "./render/scene";
import { loadBest, saveBest } from "./storage";
import { createActTool, type Hit } from "./tools/act";
import { createGrabTool } from "./tools/grab";
import { createPlaceTool } from "./tools/place";
import { initDebug } from "./ui/debug";
import { initHelp } from "./ui/help";
import { initHud } from "./ui/hud";
import { initTooltips } from "./ui/tooltips";

/**
 * Runs the game once Rapier has loaded: builds the world, the scene, the
 * tools and the UI, wires them together and starts the frame loop.
 */
export function startGame(RAPIER: Rapier): void {
  const MATERIALS = createMaterials(MATERIAL_DEFS);
  const { scene, renderer, camera, sun, marker } = createScene();
  const canvas = renderer.domElement;
  const { world, baseCollider } = createWorld(RAPIER);
  const blocks = createBlocks(RAPIER, world, scene);
  const rig = createCameraRig(camera, sun);

  // Simulated seconds: advances only by the physics steps actually run, so block
  // ages and the drop rules stay in step with the physics through slow frames
  // and stalls. Read-only on window so the e2e tests can wait on it.
  let simTime = 0;
  Object.defineProperty(window, "__simTime", { get: () => simTime });
  let stepAcc = 0;
  let mode: Mode = "place";
  let selected: { block: Block; local: THREE.Vector3 } | null = null; // touch: the block chosen for Push or Remove, awaiting confirmation
  let best = loadBest();
  let shownH = 0;

  const place = createPlaceTool(scene, blocks, rig, camera, MATERIALS[0], SHAPES[0]);
  const grab = createGrabTool(blocks, rig, canvas);
  const act = createActTool(blocks, rig, canvas, () => ({ mode, grabbing: !!grab.held }));

  const tips = initTooltips();
  const hud = initHud(MATERIALS, SHAPES, tips, {
    material: selectMat,
    shape: selectShape,
    mode: setMode,
    rotate: () => rotateBy(1),
    clear() {
      act.setHovered(null);
      blocks.clear();
    },
    fit: () => rig.fitAll(blocks.list),
    top: rig.topView,
    reset: rig.resetView,
    act() {
      if (mode === "place") place.drop(simTime);
      else actOnSelected();
    },
    hold(down) {
      holdingDrop = down && mode === "place";
      if (holdingDrop) dropHeld();
      return holdingDrop;
    },
  });
  // While Drop is held, the next block drops as soon as the last one has touched
  // down (or fallen off), so a held button builds a tower one block at a time
  let holdingDrop = false;
  let heldDrop: Block | undefined;
  function dropHeld() {
    place.drop(simTime);
    heldDrop = blocks.list[blocks.list.length - 1];
  }
  const help = initHelp(() => {
    tips.hide();
    keyboard.clearHeld();
  });
  const keyboard = initKeyboard(rig, {
    helpOpen: help.isOpen,
    materials: MATERIALS.length,
    onAction,
  });
  const input = initPointer(canvas, {
    rig,
    place,
    grab,
    pick: act.pick,
    mode: () => mode,
    onTap: handleTap,
    onInputChange: updateActBtn,
    onLeave: () => act.setHovered(null),
  });
  const debug = initDebug({
    RAPIER,
    world,
    baseCollider,
    blocks,
    renderer,
    rig,
    status: () => [
      ["Tower", `${shownH.toFixed(2)} m (best ${best.toFixed(2)})`],
      [
        "Tool",
        mode +
          (grab.held ? " (holding)" : "") +
          (mode === "place" ? `, drop ${place.clearance().toFixed(1)} m` : ""),
      ],
      ["Input", input.touch ? "touch" : "mouse"],
      ["Gesture", input.gesture ? input.gesture.type : "none"],
    ],
  });

  /* =====================================================================
     TOOLS AND MODES
     ===================================================================== */
  function selectMat(i: number) {
    place.setMaterial(MATERIALS[i]);
    hud.showMaterial(i);
    setMode("place");
  }
  function selectShape(i: number) {
    place.setShape(SHAPES[i]);
    hud.showShape(i);
    setMode("place");
  }
  function rotateBy(dir: number) {
    if (grab.held)
      grab.turn(dir); // turn the held block
    else place.turn(dir); // turn the preview
  }
  function setMode(m: Mode) {
    mode = m;
    hud.showMode(m);
    place.setVisible(m === "place");
    grab.end();
    selected = null;
    act.setHovered(null);
    updateActBtn();
  }
  function updateActBtn() {
    hud.showActButton(mode, input.touch, !!selected);
  }
  function actOnBlock(hit: Hit | null) {
    act.act(hit, hud.pushSpeed());
  }

  function handleTap(e: PointerEvent) {
    if (mode === "grab") return;
    if (e.pointerType === "mouse") {
      // With a mouse the aim only changes when the mouse moves, so repeated
      // clicks on one spot stack straight up
      if (mode === "place") place.drop(simTime);
      else actOnBlock(act.pick(e.clientX, e.clientY));
      return;
    }
    if (mode === "place") {
      place.aimAt(e.clientX, e.clientY, true);
      return;
    } // touch: a tap aims, the Drop button confirms
    const hit = act.pick(e.clientX, e.clientY);
    if (hit && selected && hit.block === selected.block) {
      actOnSelected();
      return;
    } // second tap confirms
    selected = hit
      ? { block: hit.block, local: hit.block.mesh.worldToLocal(hit.point.clone()) }
      : null;
    updateActBtn();
  }
  function actOnSelected() {
    if (!selected) return;
    const b = selected.block;
    const point = b.mesh.localToWorld(selected.local.clone());
    const dir = point.clone().sub(camera.position).normalize();
    if (mode === "remove") selected = null;
    actOnBlock({ block: b, point, dir });
    updateActBtn();
  }
  function primaryAction() {
    if (mode === "place") place.drop(simTime);
    else if (input.touch && selected) actOnSelected();
    else if (input.mouse) actOnBlock(act.pick(input.mouse.x, input.mouse.y));
  }

  function onAction(action: KeyAction) {
    switch (action.type) {
      case "help":
        help.open();
        break;
      case "debug":
        debug.toggle();
        break;
      case "material":
        selectMat(action.index);
        break;
      case "rotate":
        rotateBy(action.dir);
        break;
      case "primary":
        primaryAction();
        break;
      case "mode":
        setMode(action.mode);
        break;
      case "up":
        if (grab.held) grab.lift(0.25);
        else place.nudgeClearance(1);
        break;
      case "down":
        if (grab.held) grab.lift(-0.25);
        else place.nudgeClearance(-1);
        break;
    }
  }

  /* =====================================================================
     GAME LOOP
     ===================================================================== */
  const timer = new THREE.Timer();

  function loop(timestamp: number) {
    requestAnimationFrame(loop);
    const tFrame = performance.now();
    timer.update(timestamp);
    const rawDt = timer.getDelta(); // real time since last frame (for the debug panel)
    const dt = Math.min(rawDt, 0.1);

    // Fixed timestep: run as many 1/120 s steps as the elapsed time needs (at most 8)
    const tPhys = performance.now();
    grab.steer();
    let steps;
    ({ acc: stepAcc, steps } = stepFixed(world, stepAcc, dt));
    simTime += steps * STEP;
    const physMs = performance.now() - tPhys;

    blocks.sync();

    const { cam } = rig;
    const { settledTop, localTop } = measure(
      blocks.list,
      simTime,
      grab.held?.block ?? null,
      cam.fx,
      cam.fz,
    );
    if (
      holdingDrop &&
      mode === "place" &&
      (!heldDrop || touchedDown(heldDrop, simTime) || !blocks.list.includes(heldDrop))
    )
      dropHeld();
    if (settledTop > best + 0.01) {
      best = settledTop;
      saveBest(best);
    }
    shownH += (settledTop - shownH) * Math.min(1, dt * 6);
    hud.showScore(shownH, best, blocks.placed, blocks.fallenTotal);
    marker.visible = best > 0.2;
    marker.position.y = best;

    rig.follow(dt, localTop);
    place.update(dt, localTop, mode === "place");
    keyboard.camera(dt);
    rig.update(dt);

    if (selected && !blocks.list.includes(selected.block)) {
      selected = null;
      updateActBtn();
    }
    const g = input.gesture;
    if (input.touch) {
      // Touch: outline the selected block
      act.setHovered(
        grab.held ? grab.held.block : mode !== "place" && selected ? selected.block : null,
      );
    } else if (mode !== "place" && input.mouse && !(g && g.type !== "pending")) {
      // Mouse: blocks move under a still cursor, so re-check the hovered block every frame
      const h = act.pick(input.mouse.x, input.mouse.y);
      act.setHovered(h ? h.block : null);
    }

    const tRender = performance.now();
    renderer.render(scene, camera);
    const now = performance.now();
    debug.frame(rawDt, physMs, now - tRender, now - tFrame, steps);
  }

  help.showOnFirstVisit();
  updateActBtn();
  requestAnimationFrame(loop);
}
